import { useState, useEffect } from "react";
import axios from "axios";
import "../pages/Auth.css";

function AuthField({ label, value, onChange, type = "text", placeholder, required }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{
        display: "block", fontSize: 10, color: "var(--orange)",
        fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.2em",
        textTransform: "uppercase", marginBottom: 6,
      }}>
        &gt; {label}{required && <span style={{ opacity: 0.6 }}> *</span>}
      </label>
      <input
        type={type} value={value} onChange={onChange}
        placeholder={placeholder} required={required}
        style={{
          width: "100%", padding: "12px 14px", boxSizing: "border-box",
          background: "var(--ink-2)", color: "var(--paper)",
          border: "2px solid var(--orange)",
          fontFamily: "'JetBrains Mono', monospace", fontSize: 15, outline: "none",
          transition: "border-color 0.15s",
        }}
      />
    </div>
  );
}

const switchBtnStyle = {
  background: "transparent", border: "none",
  color: "var(--orange)", fontFamily: "inherit", fontSize: "inherit",
  textDecoration: "underline", cursor: "pointer",
  textTransform: "uppercase", letterSpacing: "0.1em", padding: 0,
};

export default function AuthModal({ mode: initialMode = "signin", onClose, setUser }) {
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ username: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const switchMode = (m) => { setMode(m); setError(""); setSuccess(""); };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    setLoading(true);
    try {
      if (mode === "signin") {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/auth/login/`,
          { username: form.username, password: form.password }
        );
        localStorage.setItem("access_token", res.data.access);
        localStorage.setItem("refresh_token", res.data.refresh);
        setUser(res.data.user);
        onClose();
        window.location.reload();

      } else if (mode === "signup") {
        if (form.password !== form.confirm) { setError("Passwords do not match."); setLoading(false); return; }
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/auth/register/`,
          { username: form.username, email: form.email, password: form.password }
        );
        localStorage.setItem("access_token", res.data.access);
        localStorage.setItem("refresh_token", res.data.refresh);
        setUser(res.data.user);
        onClose();
        window.location.reload();

      } else if (mode === "forgot") {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/auth/password-reset/`,
          { email: form.email }
        );
        setSuccess("If an account with that email exists, a reset link has been sent. Check your inbox.");
      }
    } catch (err) {
      if (mode === "signin") setError("Invalid username or password.");
      else if (mode === "signup") setError(err.response?.data?.error || "Registration failed. Please try again.");
      else setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const modeLabel  = { signin: "// IDENTIFY_YOURSELF", signup: "// NEW_CONTRIBUTOR", forgot: "// RESET_ACCESS" }[mode];
  const modeSub    = { signin: "Welcome back. Opinions await.", signup: "No email required. Just a handle and a password.", forgot: "We'll send you a link to reset your password." }[mode];

  const modeTitle = {
    signin: <><span>SIGN </span><span style={{ color: "var(--orange)" }}>IN</span></>,
    signup: <><span>ROUGH </span><span style={{ color: "var(--orange)" }}>CUT</span></>,
    forgot: <><span>RESET </span><span style={{ color: "var(--orange)" }}>ACCESS</span></>,
  }[mode];

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 2000,
        background: "rgba(10,10,10,0.85)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
        backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="auth-modal-form"
        style={{
          background: "var(--ink)", color: "var(--paper)",
          border: "3px solid var(--orange)",
          padding: 32, width: "100%", maxWidth: 440,
          boxShadow: "8px 8px 0 rgba(255,90,31,0.25)",
          position: "relative",
        }}
      >
        <button type="button" onClick={onClose} style={{
          position: "absolute", top: 12, right: 12,
          background: "transparent", border: "none",
          color: "var(--orange)", fontSize: 22, cursor: "pointer",
          lineHeight: 1, fontFamily: "'Archivo Black', sans-serif",
        }}>✕</button>

        <div style={{
          fontSize: 11, color: "var(--orange)", letterSpacing: "0.25em",
          textTransform: "uppercase", marginBottom: 10,
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          {modeLabel}
        </div>

        <h2 className="display" style={{
          fontSize: 44, color: "var(--paper)", lineHeight: 0.9,
          letterSpacing: "-0.02em", marginTop: 0, marginBottom: 6,
        }}>
          {modeTitle}
        </h2>

        <p style={{ fontSize: 14, color: "var(--gray)", marginBottom: 22, lineHeight: 1.5 }}>
          {modeSub}
        </p>

        {error && (
          <div style={{
            padding: "10px 12px", marginBottom: 14,
            background: "var(--orange)", color: "var(--ink)",
            fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
            letterSpacing: "0.08em", textTransform: "uppercase",
            border: "2px solid var(--ink)",
          }}>
            ✕ {error}
          </div>
        )}
        {success && (
          <div style={{
            padding: "10px 12px", marginBottom: 14,
            background: "rgba(74,222,128,0.08)", color: "#4ade80",
            fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
            letterSpacing: "0.08em", border: "2px solid #4ade80",
          }}>
            {success}
          </div>
        )}

        {mode !== "forgot" && (
          <AuthField label="USERNAME" value={form.username} onChange={upd("username")}
            placeholder="crit_goblin" required />
        )}
        {(mode === "signup" || mode === "forgot") && (
          <AuthField
            label={mode === "signup" ? "EMAIL (OPTIONAL)" : "EMAIL ADDRESS"}
            type="email" value={form.email} onChange={upd("email")}
            placeholder="you@somewhere.net" required={mode === "forgot"}
          />
        )}
        {mode !== "forgot" && (
          <AuthField label="PASSWORD" type="password" value={form.password}
            onChange={upd("password")} placeholder="••••••••" required />
        )}
        {mode === "signup" && (
          <AuthField label="CONFIRM PASSWORD" type="password" value={form.confirm}
            onChange={upd("confirm")} placeholder="••••••••" required />
        )}

        {!success && (
          <button
            type="submit"
            disabled={loading}
            className="btn btn-solid"
            style={{
              width: "100%", justifyContent: "center",
              padding: 14, marginBottom: 14, fontSize: 14,
              opacity: loading ? 0.5 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "LOADING…"
              : mode === "signup" ? "CREATE ACCOUNT ↗︎"
              : mode === "forgot" ? "SEND RESET LINK ↗︎"
              : "SIGN IN ↗︎"}
          </button>
        )}

        <div style={{
          textAlign: "center", paddingTop: 12,
          borderTop: "1px dashed var(--gray-dim)",
          fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
          color: "var(--gray)", letterSpacing: "0.1em", textTransform: "uppercase",
          lineHeight: 2,
        }}>
          {mode === "signin" && (<>
            <span>NEW AROUND HERE? </span>
            <button type="button" onClick={() => switchMode("signup")} style={switchBtnStyle}>CREATE ACCOUNT</button>
            <br />
            <button type="button" onClick={() => switchMode("forgot")} style={switchBtnStyle}>FORGOT PASSWORD?</button>
          </>)}
          {mode === "signup" && (<>
            <span>ALREADY A CONTRIBUTOR? </span>
            <button type="button" onClick={() => switchMode("signin")} style={switchBtnStyle}>SIGN IN</button>
          </>)}
          {mode === "forgot" && (<>
            <span>REMEMBER IT? </span>
            <button type="button" onClick={() => switchMode("signin")} style={switchBtnStyle}>BACK TO SIGN IN</button>
          </>)}
        </div>
      </form>
    </div>
  );
}
